import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { SessionStorageService } from '@services/storage.service';
import {
	ICreatePolicy,
	ICreatePolicyMap,
	IPermission,
	IPolicy,
	IPolicyMap,
	IPolicyMapDetail,
	IScimGroup,
	IScimGroupDetail,
	IScimListResponse,
	IScimSingleResponse,
	IScimToken,
	IScimUser,
	IScimUserDetail,
	IService,
	PermissionSchema,
	PolicyMapSchema,
	PolicySchema,
	ScimGroupSchema,
	ScimUserSchema,
	ServiceSchema,
} from '@shared/models/scim-admin';
import { UserService } from '@shared/services/user.service';
import { EMPTY, expand, map, Observable, reduce, retry } from 'rxjs';
import { z } from 'zod';

const PAGE_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class ScimAdminService {
	private basePath = '/iam/scimadmin';

	constructor(
		private http: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {}

	private get headers(): HttpHeaders {
		const token = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({ Authorization: `Bearer ${token}` });
	}

	private get organizationId(): string {
		return this.userService.account.organization.id;
	}

	private get accountId(): string {
		return this.userService.account.id;
	}

	private parseResponseItem<T>(schema: z.ZodType<T>, item: unknown, baseUrl: string, index?: number): T {
		const result = schema.safeParse(item);
		if (result.success) {
			return result.data;
		}

		const itemContext = index === undefined ? '' : ` (item index ${index})`;
		const error = new Error(`Invalid API response from ${baseUrl}${itemContext}: ${result.error.message}`);
		(error as Error & { cause: z.ZodError }).cause = result.error;
		throw error;
	}

	private fetchAllPages<T>(baseUrl: string, schema?: z.ZodType<T>): Observable<T[]> {
		let offset = 0;
		return this.http.get<IScimListResponse<T>>(`${baseUrl}&offset=${offset}`, { headers: this.headers }).pipe(
			expand((response) => {
				if (response.result.length >= PAGE_SIZE) {
					offset += PAGE_SIZE;
					return this.http.get<IScimListResponse<T>>(`${baseUrl}&offset=${offset}`, {
						headers: this.headers,
					});
				}
				return EMPTY;
			}),
			reduce((acc: T[], response) => [...acc, ...response.result], []),
			map((items) => (schema ? items.map((item, index) => this.parseResponseItem(schema, item, baseUrl, index)) : items)),
		);
	}

	// Users
	getUsers(): Observable<IScimUser[]> {
		const url = `${environment.awsEnvUri}${this.basePath}/users?organizationId=${this.organizationId}&accountId=${this.accountId}`;
		return this.fetchAllPages<IScimUser>(url, ScimUserSchema);
	}

	getUser(userId: string): Observable<IScimUserDetail> {
		const url = `${environment.awsEnvUri}${this.basePath}/users/${userId}`;
		return this.http.get<IScimSingleResponse<IScimUserDetail>>(url, { headers: this.headers }).pipe(
			retry(1),
			map((response) => this.parseResponseItem(ScimUserSchema, response.result, url)),
		);
	}

	deleteUser(userId: string): Observable<object> {
		return this.http
			.delete(`${environment.awsEnvUri}${this.basePath}/users/${userId}`, {
				headers: this.headers,
			})
			.pipe(retry(1));
	}

	// Groups
	getGroups(): Observable<IScimGroup[]> {
		const url = `${environment.awsEnvUri}${this.basePath}/groups?organizationId=${this.organizationId}&accountId=${this.accountId}`;
		return this.fetchAllPages<IScimGroup>(url, ScimGroupSchema);
	}

	getGroup(groupId: string): Observable<IScimGroupDetail> {
		const url = `${environment.awsEnvUri}${this.basePath}/groups/${groupId}`;
		return this.http.get<IScimSingleResponse<IScimGroupDetail>>(url, { headers: this.headers }).pipe(
			retry(1),
			map((response) => this.parseResponseItem(ScimGroupSchema, response.result, url)),
		);
	}

	deleteGroup(groupId: string): Observable<object> {
		return this.http
			.delete(`${environment.awsEnvUri}${this.basePath}/groups/${groupId}`, {
				headers: this.headers,
			})
			.pipe(retry(1));
	}

	patchGroupRemoveMembers(groupId: string, userIds: string[]): Observable<IScimGroup> {
		const url = `${environment.awsEnvUri}${this.basePath}/groups/${groupId}`;
		const body = {
			members: userIds.map((userId) => ({ op: 'remove', userId })),
		};
		return this.http.patch<IScimSingleResponse<IScimGroup>>(url, body, { headers: this.headers }).pipe(map((response) => this.parseResponseItem(ScimGroupSchema, response.result, url)));
	}

	// Policy Maps
	getPolicyMaps(): Observable<IPolicyMap[]> {
		const url = `${environment.awsEnvUri}${this.basePath}/policymaps?organizationId=${this.organizationId}&accountId=${this.accountId}`;
		return this.fetchAllPages<IPolicyMap>(url, PolicyMapSchema);
	}

	getPolicyMap(policyMapId: string): Observable<IPolicyMapDetail> {
		const url = `${environment.awsEnvUri}${this.basePath}/policymaps/${policyMapId}`;
		return this.http.get<IScimSingleResponse<IPolicyMapDetail>>(url, { headers: this.headers }).pipe(
			retry(1),
			map((response) => this.parseResponseItem(PolicyMapSchema, response.result, url)),
		);
	}

	createPolicyMap(data: ICreatePolicyMap): Observable<IPolicyMap> {
		const url = `${environment.awsEnvUri}${this.basePath}/policymaps`;
		return this.http.post<IScimSingleResponse<IPolicyMap>>(url, data, { headers: this.headers }).pipe(map((response) => this.parseResponseItem(PolicyMapSchema, response.result, url)));
	}

	deletePolicyMap(policyMapId: string): Observable<object> {
		return this.http
			.delete(`${environment.awsEnvUri}${this.basePath}/policymaps/${policyMapId}`, {
				headers: this.headers,
			})
			.pipe(retry(1));
	}

	// Token
	getToken(): Observable<IScimToken> {
		return this.http
			.get(`${environment.awsEnvUri}${this.basePath}/token?accountId=${this.accountId}`, {
				headers: this.headers,
				responseType: 'text',
			})
			.pipe(
				retry(1),
				map((response) => ({ token: response })),
			);
	}

	// Policies (from /iam/policies - managed level)
	getPolicies(): Observable<IPolicy[]> {
		const url = `${environment.awsEnvUri}/iam/policies?placeholder=1`;
		return this.fetchAllPages<IPolicy>(url, PolicySchema);
	}

	// Policies at organization level
	getOrganizationPolicies(): Observable<IPolicy[]> {
		const url = `${environment.awsEnvUri}/iam/organizations/${this.organizationId}/policies?placeholder=1`;
		return this.fetchAllPages<IPolicy>(url, PolicySchema);
	}

	// Policies at account level
	getAccountPolicies(): Observable<IPolicy[]> {
		const url = `${environment.awsEnvUri}/iam/organizations/${this.organizationId}/accounts/${this.accountId}/policies?placeholder=1`;
		return this.fetchAllPages<IPolicy>(url, PolicySchema);
	}

	// Services
	getServices(): Observable<IService[]> {
		const url = `${environment.awsEnvUri}/iam/services?placeholder=1`;
		return this.fetchAllPages<IService>(url, ServiceSchema);
	}

	// Service Permissions
	getServicePermissions(serviceId: string): Observable<IPermission[]> {
		const url = `${environment.awsEnvUri}/iam/services/${serviceId}/permissions?placeholder=1`;
		return this.fetchAllPages<IPermission>(url, PermissionSchema);
	}

	// Create organization policy
	createOrganizationPolicy(data: ICreatePolicy): Observable<IPolicy> {
		const url = `${environment.awsEnvUri}/iam/organizations/${this.organizationId}/policies`;
		return this.http.post<IScimSingleResponse<IPolicy>>(url, data, { headers: this.headers }).pipe(map((response) => this.parseResponseItem(PolicySchema, response.result, url)));
	}

	// Create account policy
	createAccountPolicy(data: ICreatePolicy): Observable<IPolicy> {
		const url = `${environment.awsEnvUri}/iam/organizations/${this.organizationId}/accounts/${this.accountId}/policies`;
		return this.http.post<IScimSingleResponse<IPolicy>>(url, data, { headers: this.headers }).pipe(map((response) => this.parseResponseItem(PolicySchema, response.result, url)));
	}

	// Update organization policy
	updateOrganizationPolicy(policyId: string, data: ICreatePolicy): Observable<IPolicy> {
		const url = `${environment.awsEnvUri}/iam/organizations/${this.organizationId}/policies/${policyId}`;
		return this.http.put<IScimSingleResponse<IPolicy>>(url, data, { headers: this.headers }).pipe(map((response) => this.parseResponseItem(PolicySchema, response.result, url)));
	}

	// Update account policy
	updateAccountPolicy(policyId: string, data: ICreatePolicy): Observable<IPolicy> {
		const url = `${environment.awsEnvUri}/iam/organizations/${this.organizationId}/accounts/${this.accountId}/policies/${policyId}`;
		return this.http.put<IScimSingleResponse<IPolicy>>(url, data, { headers: this.headers }).pipe(map((response) => this.parseResponseItem(PolicySchema, response.result, url)));
	}

	// Delete organization policy
	deleteOrganizationPolicy(policyId: string): Observable<object> {
		return this.http.delete(`${environment.awsEnvUri}/iam/organizations/${this.organizationId}/policies/${policyId}`, { headers: this.headers });
	}

	// Delete account policy
	deleteAccountPolicy(policyId: string): Observable<object> {
		return this.http.delete(`${environment.awsEnvUri}/iam/organizations/${this.organizationId}/accounts/${this.accountId}/policies/${policyId}`, { headers: this.headers });
	}
}
